/* eslint-disable */
import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Sidebar from "../../components/sidebar/Sidebar2";
import Navbar from "../../components/navbar/Navbar2";
import Footer from "../../components/footer/Footer";
import StatisticsCard from "../../components/statistics/StatisticsCard";
import StatisticsChart from "../../components/statistics/StatisticsChart";
import DeliveryStatisticsView from "../../components/statistics/DeliveryStatisticsView";
import { deliveryStatusMapping } from "../../constants/deliveryStatusMapping";
import CrispChat from "../../components/CrispChat";
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  Clock,
  Calendar,
  Copy,
  Filter,
} from "lucide-react";
import { DatePicker, Spin, Tabs } from "antd";
import dayjs from "dayjs";
import "./OrderStatistics.css";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import { fetchWorkers } from "../../redux/workers/workers";

const OrderStatistics = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  // Redux state
  const dispatch = useDispatch();
  const orders = useSelector((state) => state.OrdersReducer);
  const products = useSelector((state) => state.ProductsReducer);
  const account = useSelector((state) => state.AccountReducer);
  const workers = useSelector((state) => state.workersReducer?.workers || []);

  // Local state
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [activeTab, setActiveTab] = useState("1"); // Add active tab state
  const [dateRange, setDateRange] = useState("week");
  const [customDateRange, setCustomDateRange] = useState([null, null]);
  const [selected, setSelected] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isConfirmerOpen, setIsConfirmerOpen] = useState(false);
  const [selectedConfirmerId, setSelectedConfirmerId] = useState("all");
  const [loading, setLoading] = useState(false);
  const [showSideBar, setShowSideBar] = useState(true);

  const confirmers = useMemo(() => {
    return (workers || []).filter(
      (worker) =>
        worker.ownerId === account?.user &&
        worker.role === "confirmer" &&
        worker.deleted !== true,
    );
  }, [workers, account?.user]);

  // Fetch confirmers from Firestore
  useEffect(() => {
    if (!account?.user) return;

    const hasCurrentUserConfirmers = workers.some(
      (worker) =>
        worker.ownerId === account.user &&
        worker.role === "confirmer" &&
        worker.deleted !== true,
    );

    if (hasCurrentUserConfirmers) return;

    const fetchConfirmersOnce = async () => {
      try {
        const confirmersQuery = query(
          collection(db, "workers"),
          where("ownerId", "==", account.user),
          where("role", "==", "confirmer"),
          where("deleted", "!=", true),
        );

        const snapshot = await getDocs(confirmersQuery);
        const list = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        const otherWorkers = workers.filter(
          (worker) =>
            !(worker.ownerId === account.user && worker.role === "confirmer"),
        );

        dispatch(fetchWorkers([...otherWorkers, ...list]));
      } catch (error) {
        console.error("Error fetching confirmers:", error);
      }
    };

    fetchConfirmersOnce();
  }, [account?.user, workers, dispatch]);

  // Handle sidebar resize
  useEffect(() => {
    function handleResize() {
      if (window.innerWidth <= 1080) {
        setShowSideBar(false);
      } else {
        setShowSideBar(true);
      }
    }

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const toggleProduct = (name) => {
    setSelected((prev) =>
      prev.includes(name)
        ? prev.filter((pname) => pname !== name)
        : [...prev, name],
    );
  };

  // Get date range based on selection
  const getDateRangeValues = () => {
    const now = new Date();
    let startDate = new Date(now);
    let endDate = new Date(now);

    switch (dateRange) {
      case "today":
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        break;
      case "yesterday":
        startDate.setDate(now.getDate() - 1);
        startDate.setHours(0, 0, 0, 0);
        endDate.setDate(now.getDate() - 1);
        endDate.setHours(23, 59, 59, 999);
        break;
      case "week":
        startDate.setDate(now.getDate() - 7);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        break;
      case "month":
        startDate.setMonth(now.getMonth() - 1);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        break;
      case "custom":
        if (customDateRange[0] && customDateRange[1]) {
          startDate = new Date(customDateRange[0]);
          endDate = new Date(customDateRange[1]);
          startDate.setHours(0, 0, 0, 0);
          endDate.setHours(23, 59, 59, 999);
        }
        break;
      default:
        startDate.setDate(now.getDate() - 7);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
    }

    return { startDate, endDate };
  };

  // Filter orders
  useEffect(() => {
    setLoading(true);
    const { startDate, endDate } = getDateRangeValues();

    let filtered = orders.filter((order) => {
      // Date filter
      if (order.timeStamp?.seconds) {
        const orderDate = new Date(order.timeStamp.seconds * 1000);
        if (orderDate < startDate || orderDate > endDate) return false;
      }

      // Product filter
      if (selected.length > 0) {
        const hasSelectedProduct = order.products?.some((product) =>
          selected.includes(product.name),
        );
        if (!hasSelectedProduct) return false;
      }

      // Confirmer filter (same logic as /orders)
      if (confirmers.length) {
        if (selectedConfirmerId === "unassigned") {
          if (order?.assignedConfirmerId) return false;
        } else if (selectedConfirmerId !== "all") {
          if (order?.assignedConfirmerId !== selectedConfirmerId) {
            return false;
          }
        }
      }

      return true;
    });

    setFilteredOrders(filtered);
    setLoading(false);
  }, [
    orders,
    dateRange,
    customDateRange,
    selected,
    selectedConfirmerId,
    confirmers,
  ]);

  // Calculate statistics
  const statistics = useMemo(() => {
    // Helper function to count unique days in timeline
    const getUniqueDaysCount = (statusTimeline) => {
      if (!Array.isArray(statusTimeline)) return 0;

      const uniqueDates = new Set();

      statusTimeline.forEach((item) => {
        if (item.timestamp) {
          let timestamp = item.timestamp;

          // Validate timestamp and ensure it's a valid date
          if (timestamp.seconds) {
            timestamp = new Date(timestamp.seconds * 1000);
          } else if (isNaN(new Date(timestamp))) {
            return; // Skip invalid timestamp
          } else {
            timestamp = new Date(timestamp); // fallback
          }

          const dayString = timestamp.toISOString().split("T")[0]; // YYYY-MM-DD
          uniqueDates.add(dayString);
        }
      });

      return uniqueDates.size;
    };

    const stats = {
      new: 0, // 1
      confirmed: 0, // 3, 9
      cancelled: 0, // 6, 11-5, 6-2, 6-1, 6-4
      failed: 0, // 11-4, 11-2 with timeline > 3 days
      pending: 0, // 11-1, 11-6 without timeline > 3 days
      postponed: 0, // 11-3
      total: filteredOrders.length,
    };

    filteredOrders.forEach((order) => {
      const status = String(order.deleveryStatus || "");
      const uniqueDays = getUniqueDaysCount(order.statusTimeline);
      const timeline = Array.isArray(order.statusTimeline)
        ? order.statusTimeline
        : [];

      const wasConfirmed =
        status === "3" ||
        status === "9" ||
        timeline.some((e) => {
          const s = String(e?.status || "");
          return s === "3" || s === "9";
        });

      if (wasConfirmed) {
        stats.confirmed += 1;
      }

      if (
        status === "6" ||
        status === "11-5" ||
        status === "6-2" ||
        status === "6-1" ||
        status === "6-4"
      ) {
        stats.cancelled += 1;
      } else if ((status === "11-4" || status === "11-2") && uniqueDays >= 3) {
        stats.failed += 1;
      } else if ((status === "11-1" || status === "11-6") && uniqueDays <= 3) {
        stats.pending += 1;
      } else if (status === "11-3") {
        stats.postponed += 1;
      } else if (status === "1") {
        stats.new += 1;
      }
    });

    return stats;
  }, [filteredOrders]);

  const { startDate, endDate } = getDateRangeValues();

  // Worker/Confirmer options
  const confirmerOptions = useMemo(() => {
    return confirmers.map((w) => ({
      label: w.name || w.email,
      value: w.id,
    }));
  }, [confirmers]);

  const selectedConfirmerLabel = useMemo(() => {
    if (selectedConfirmerId === "all") {
      return t("adduser.all_confirmers");
    }
    if (selectedConfirmerId === "unassigned") {
      return t("orders.datatable_templating_demo.unassigned");
    }
    const found = confirmers.find((c) => c.id === selectedConfirmerId);
    return found?.name || found?.email || t("stats.select_confirmer");
  }, [selectedConfirmerId, confirmers, t]);

  const getStatusDescription = (codes) => {
    if (!codes || codes.length === 0) return null;

    return (
      <ul className="text-xs space-y-1">
        {codes.map((code, index) => (
          <li key={index} className="text-gray-800 dark:text-gray-100">
            • {t(`stats.status_codes.${code}`, code)}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div
      className="container-fluid bg-purple-50 dark:!bg-[#181919] min-h-screen"
      dir={isRTL ? "rtl" : "ltr"}
    >
      <div className="row" style={{ display: "flex", minHeight: "100vh" }}>
        {showSideBar && (
          <div
            style={{
              width: "250px",
              flexShrink: 0,
              transition: "width 0.6s ease",
              boxShadow: "2px 0 10px rgba(0,0,0,0.05)",
            }}
          >
            <Sidebar
              showSideBar={showSideBar}
              setShowSideBar={setShowSideBar}
            />
          </div>
        )}
        <div
          className="px-3 bg-[#fbf8fc] dark:!bg-[#141414]"
          style={{
            flex: 1,
            minWidth: 0,
            overflow: "auto",
          }}
        >
          <Navbar showSideBar={showSideBar} setShowSideBar={setShowSideBar} />
          <CrispChat />
          <main className="flex-1 p-6">
            <Spin spinning={loading}>
              {/* Header */}
              <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 dark:!text-gray-100 mb-2">
                  {t("stats.title") || t("stats.title")}
                </h1>
                <p className="text-gray-600 dark:!text-gray-400">
                  {t("stats.description") || t("stats.description")}
                </p>
              </div>

              {/* Tabs Section */}
              <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                className="mb-8 statistics-tabs"
                items={[
                  {
                    key: "1",
                    label: t("stats.orders_statistics"),
                    children: (
                      <>
                        {/* Filters Section */}
                        <div className="bg-white dark:!bg-[#181919] rounded-xl shadow-sm p-4 mb-8 border border-purple-100 dark:!border-[#3c3a3d]">
                          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                            <div className="flex items-center gap-2">
                              <Filter size={20} className="text-[#9e68ba]" />
                              <h2 className="text-lg font-semibold text-[#9e68ba]">
                                {t("stats.filters") || t("stats.filters")}
                              </h2>
                            </div>
                          </div>

                          <div className="flex flex-wrap items-center gap-3">
                            <div className="bg-white dark:!bg-[#181919] rounded-xl shadow-sm border border-purple-100 dark:!border-[#3c3a3d] overflow-hidden">
                              <div className="flex">
                                {[
                                  {
                                    value: "today",
                                    label: t("stats.today") || "Aujourd'hui",
                                  },
                                  {
                                    value: "yesterday",
                                    label: t("stats.yesterday") || "Hier",
                                  },
                                  {
                                    value: "week",
                                    label: t("stats.week") || "Semaine",
                                  },
                                  {
                                    value: "month",
                                    label: t("stats.month") || "Mois",
                                  },
                                ].map((option) => (
                                  <button
                                    key={option.value}
                                    className={`px-6 py-3 text-sm font-medium transition-colors ${
                                      dateRange === option.value
                                        ? "bg-purple-50 dark:!bg-[#522868] dark:!text-gray-300 text-[#9e68ba] border-b-2 border-[#9b59bf]"
                                        : "text-gray-600 hover:bg-purple-50 dark:!text-gray-300"
                                    }`}
                                    onClick={() => {
                                      setDateRange(option.value);
                                      setCustomDateRange([null, null]);
                                    }}
                                  >
                                    {option.label}
                                  </button>
                                ))}
                              </div>
                            </div>

                            <div className="bg-white dark:!bg-[#181919] rounded-xl shadow-sm border border-purple-100 dark:!border-[#3c3a3d]">
                              <DatePicker.RangePicker
                                value={
                                  customDateRange[0] && customDateRange[1]
                                    ? [
                                        dayjs(customDateRange[0]),
                                        dayjs(customDateRange[1]),
                                      ]
                                    : null
                                }
                                onChange={(dates) => {
                                  if (dates) {
                                    setCustomDateRange([
                                      dates[0].toDate(),
                                      dates[1].toDate(),
                                    ]);
                                    setDateRange("custom");
                                  } else {
                                    setCustomDateRange([null, null]);
                                  }
                                }}
                                className="w-full"
                                style={{
                                  backgroundColor:
                                    document.documentElement.classList.contains(
                                      "dark",
                                    )
                                      ? "#181919"
                                      : "#fff",
                                }}
                              />
                            </div>

                            <div className="relative">
                              {/* Filter Button */}
                              <div
                                className="relative cursor-pointer group"
                                onClick={() => setIsOpen(!isOpen)}
                              >
                                <div className="flex items-center justify-between w-full px-4 py-2.5 bg-white dark:!bg-[#181919] border border-gray-200 dark:!border-[#3c3a3d] rounded-lg shadow-sm hover:shadow-md transition-all duration-200">
                                  {/* Filter Icon & Text */}
                                  <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-[#faf1ff] rounded-full flex items-center justify-center">
                                      <svg
                                        className="w-3 h-3 text-[#9b59bf]"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                      >
                                        <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V4z" />
                                      </svg>
                                    </div>

                                    {/* Text Content */}
                                    <span className="font-medium text-gray-900 dark:!text-gray-300">
                                      {selected.length > 0
                                        ? selected.length === 1
                                          ? t("orders.one_product")
                                          : t("orders.multiple_products", {
                                              count: selected.length,
                                            })
                                        : t("orders.select_products")}
                                    </span>
                                  </div>

                                  {/* Arrow Icon with Animation */}
                                  <div
                                    className={`transform transition-transform duration-200 ${
                                      isOpen ? "rotate-180" : ""
                                    }`}
                                  >
                                    <svg
                                      className="w-4 h-4 text-gray-500"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M19 9l-7 7-7-7"
                                      />
                                    </svg>
                                  </div>
                                </div>

                                {/* Selected count badge */}
                                {selected.length > 0 && (
                                  <div className="absolute -top-1 -right-1 bg-[#9b59bf] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold shadow-sm">
                                    {selected.length}
                                  </div>
                                )}
                              </div>

                              {/* Dropdown Menu */}
                              <div className="relative">
                                {isOpen && (
                                  <>
                                    {/* Backdrop */}
                                    <div
                                      className="fixed inset-0 bg-opacity-5 z-40"
                                      onClick={() => setIsOpen(false)}
                                    />

                                    {/* Dropdown Container */}
                                    <div
                                      className="absolute mt-1 bg-white dark:!bg-[#181919] dark:!text-gray-300 border border-gray-200 dark:!border-[#3c3a3d] rounded-lg shadow-lg max-h-60 overflow-hidden w-full animate-in slide-in-from-top-1 duration-150"
                                      style={{
                                        zIndex: 100000,
                                      }}
                                    >
                                      {/* Header */}
                                      <div className="bg-[#faf1ff] dark:!bg-[#181919] px-4 py-2.5 border-b border-purple-100 dark:!border-[#3c3a3d]">
                                        <div className="flex items-center justify-between">
                                          <span className="font-medium text-gray-800 dark:!text-gray-300 text-sm">
                                            {t("orders.available_products")}
                                          </span>
                                          {selected.length > 0 && (
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setSelected([]);
                                              }}
                                              className="text-xs text-[#9b59bf] hover:text-[#814a9f] font-medium transition-colors"
                                            >
                                              {t("orders.clear_all")}
                                            </button>
                                          )}
                                        </div>
                                      </div>

                                      {/* Products List */}
                                      <div
                                        className="max-h-48 overflow-y-auto"
                                        style={{
                                          scrollbarWidth: "thin",
                                          scrollbarColor: "#d8b4fe #f5f5f5",
                                          zIndex: 100000,
                                        }}
                                      >
                                        {products.map((product) => {
                                          const isSelected = selected.includes(
                                            product.name,
                                          );
                                          return (
                                            <label
                                              key={product.id}
                                              className={`group dark:!bg-[#181919] flex items-center gap-2 px-4 py-2.5 hover:bg-[#faf1ff] cursor-pointer transition-colors duration-150 ${
                                                isSelected
                                                  ? "bg-[#faf1ff] border-r-2 border-[#9b59bf]"
                                                  : ""
                                              }`}
                                            >
                                              {/* Custom Checkbox */}
                                              <div className="relative flex-shrink-0">
                                                <input
                                                  type="checkbox"
                                                  checked={isSelected}
                                                  onChange={() => {
                                                    toggleProduct(product.name);
                                                    setIsOpen(false);
                                                  }}
                                                  className="sr-only"
                                                />
                                                <div
                                                  className={`w-4 h-4 rounded border transition-all duration-200 flex items-center justify-center ${
                                                    isSelected
                                                      ? "bg-[#9b59bf] dark:!bg-[#181919] border-[#9b59bf]"
                                                      : "border-gray-300 hover:border-[#9b59bf]"
                                                  }`}
                                                >
                                                  {isSelected && (
                                                    <svg
                                                      className="w-3 h-3 text-white"
                                                      fill="currentColor"
                                                      viewBox="0 0 20 20"
                                                    >
                                                      <path
                                                        fillRule="evenodd"
                                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                        clipRule="evenodd"
                                                      />
                                                    </svg>
                                                  )}
                                                </div>
                                              </div>

                                              {/* Product Name */}
                                              <span
                                                className={`text-sm transition-colors ${
                                                  isSelected
                                                    ? "text-[#9b59bf] font-medium"
                                                    : "text-gray-800 dark:!text-gray-300 group-hover:text-[#9b59bf]"
                                                }`}
                                              >
                                                {product.name}
                                              </span>

                                              {/* Selection Indicator */}
                                              {isSelected && (
                                                <div className="w-1.5 h-1.5 bg-[#9b59bf] rounded-full ml-auto" />
                                              )}
                                            </label>
                                          );
                                        })}
                                      </div>

                                      {/* Empty State */}
                                      {products.length === 0 && (
                                        <div className="px-4 py-8 text-center text-gray-500">
                                          <div className="w-8 h-8 mx-auto mb-2 text-gray-300">
                                            <svg
                                              fill="currentColor"
                                              viewBox="0 0 20 20"
                                            >
                                              <path d="M10 2L3 7v11a1 1 0 001 1h12a1 1 0 001-1V7l-7-5zM10 12a2 2 0 100-4 2 2 0 000 4z" />
                                            </svg>
                                          </div>
                                          <span className="text-sm">
                                            {t("orders.no_products_available")}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                            <div className="relative">
                              {/* Confirmer Filter Button */}
                              <div
                                className="relative cursor-pointer group"
                                onClick={() =>
                                  setIsConfirmerOpen(!isConfirmerOpen)
                                }
                              >
                                <div className="flex items-center justify-between w-full px-4 py-2.5 bg-white dark:!bg-[#181919] border border-gray-200 dark:!border-[#3c3a3d] rounded-lg shadow-sm hover:shadow-md transition-all duration-200">
                                  {/* Icon & Text */}
                                  <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-[#faf1ff] rounded-full flex items-center justify-center">
                                      <svg
                                        className="w-3 h-3 text-[#9b59bf]"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                      >
                                        <path d="M10 2a8 8 0 100 16 8 8 0 000-16zm3 11H7a1 1 0 110-2h6a1 1 0 110 2zm0-4H7a1 1 0 110-2h6a1 1 0 110 2z" />
                                      </svg>
                                    </div>

                                    <span className="font-medium text-gray-900 dark:!text-gray-300">
                                      {selectedConfirmerLabel}
                                    </span>
                                  </div>

                                  {/* Arrow Icon */}
                                  <div
                                    className={`transform transition-transform duration-200 ${
                                      isConfirmerOpen ? "rotate-180" : ""
                                    }`}
                                  >
                                    <svg
                                      className="w-4 h-4 text-gray-500"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M19 9l-7 7-7-7"
                                      />
                                    </svg>
                                  </div>
                                </div>
                              </div>

                              {/* Dropdown Menu */}
                              <div className="relative">
                                {isConfirmerOpen && (
                                  <>
                                    <div
                                      className="fixed inset-0 bg-opacity-5 z-40"
                                      onClick={() => setIsConfirmerOpen(false)}
                                    />

                                    <div
                                      className="absolute mt-1 bg-white dark:!bg-[#181919] dark:!text-gray-300 border border-gray-200 dark:!border-[#3c3a3d] rounded-lg shadow-lg max-h-60 overflow-hidden w-full animate-in slide-in-from-top-1 duration-150"
                                      style={{
                                        zIndex: 100000,
                                      }}
                                    >
                                      <div className="bg-[#faf1ff] dark:!bg-[#181919] px-4 py-2.5 border-b border-purple-100 dark:!border-[#3c3a3d]">
                                        <span className="font-medium text-gray-800 dark:!text-gray-300 text-sm">
                                          {t("stats.confirmer") ||
                                            t("stats.confirmer")}
                                        </span>
                                      </div>

                                      <div className="max-h-48 overflow-y-auto">
                                        {[
                                          {
                                            label: t("adduser.all_confirmers"),
                                            value: "all",
                                          },
                                          {
                                            label: t(
                                              "orders.datatable_templating_demo.unassigned",
                                            ),
                                            value: "unassigned",
                                          },
                                          ...confirmerOptions,
                                        ].map((option) => {
                                          const isSelected =
                                            option.value ===
                                            selectedConfirmerId;
                                          return (
                                            <button
                                              type="button"
                                              key={option.value}
                                              onClick={() => {
                                                setSelectedConfirmerId(
                                                  option.value,
                                                );
                                                setIsConfirmerOpen(false);
                                              }}
                                              className={`w-full text-left px-4 py-2.5 flex items-center gap-2 hover:bg-[#faf1ff] dark:hover:bg-[#1f1f1f] transition-colors ${
                                                isSelected
                                                  ? "bg-[#faf1ff] text-[#9b59bf] font-medium"
                                                  : "text-gray-800 dark:!text-gray-300"
                                              }`}
                                            >
                                              <span className="flex-1">
                                                {option.label}
                                              </span>
                                              {isSelected && (
                                                <svg
                                                  className="w-4 h-4 text-[#9b59bf]"
                                                  fill="currentColor"
                                                  viewBox="0 0 20 20"
                                                >
                                                  <path
                                                    fillRule="evenodd"
                                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                    clipRule="evenodd"
                                                  />
                                                </svg>
                                              )}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => {
                                setDateRange("week");
                                setCustomDateRange([null, null]);
                                setSelected([]);
                                setSelectedConfirmerId("all");
                              }}
                              className="ml-auto px-4 py-2 text-sm font-medium rounded-lg bg-purple-50 text-[#9e68ba] border border-purple-100 hover:bg-purple-100 dark:!bg-[#262526] dark:!text-gray-300 dark:!border-[#3c3a3d] transition-colors"
                            >
                              {t("stats.clear_filters") ||
                                t("stats.clear_filters")}
                            </button>
                          </div>
                        </div>

                        {/* Statistics Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                          <StatisticsCard
                            title={t("stats.confirmed") || t("ui.confirmed")}
                            count={statistics.confirmed}
                            icon={CheckCircle2}
                            color="green"
                            statusTypes={getStatusDescription(["3", "9"])}
                            description={`${
                              statistics.total > 0
                                ? (
                                    (statistics.confirmed / statistics.total) *
                                    100
                                  ).toFixed(1)
                                : 0
                            }${t("stats.of_total") || "% du total"}`}
                          />

                          <StatisticsCard
                            title={t("stats.pending") || t("ui.queued")}
                            count={statistics.pending}
                            icon={Clock}
                            color="blue"
                            statusTypes={getStatusDescription(["11-1", "11-6"])}
                            description={`${
                              statistics.total > 0
                                ? (
                                    (statistics.pending / statistics.total) *
                                    100
                                  ).toFixed(1)
                                : 0
                            }${t("stats.of_total") || "% du total"}`}
                          />

                          <StatisticsCard
                            title={t("stats.postponed") || t("stats.postponed")}
                            count={statistics.postponed}
                            icon={Calendar}
                            color="yellow"
                            statusTypes={getStatusDescription(["11-3"])}
                            description={`${
                              statistics.total > 0
                                ? (
                                    (statistics.postponed / statistics.total) *
                                    100
                                  ).toFixed(1)
                                : 0
                            }${t("stats.of_total") || "% du total"}`}
                          />

                          <StatisticsCard
                            title={t("stats.failed") || t("stats.failed")}
                            count={statistics.failed}
                            icon={AlertCircle}
                            color="orange"
                            statusTypes={getStatusDescription(["11-4", "11-2"])}
                            description={`${
                              statistics.total > 0
                                ? (
                                    (statistics.failed / statistics.total) *
                                    100
                                  ).toFixed(1)
                                : 0
                            }${t("stats.of_total") || "% du total"}`}
                          />

                          <StatisticsCard
                            title={t("stats.cancelled") || t("ui.cancelled")}
                            count={statistics.cancelled}
                            icon={XCircle}
                            color="red"
                            statusTypes={getStatusDescription([
                              "6",
                              "11-5",
                              "6-2",
                              "6-1",
                              "6-4",
                            ])}
                            description={`${
                              statistics.total > 0
                                ? (
                                    (statistics.cancelled / statistics.total) *
                                    100
                                  ).toFixed(1)
                                : 0
                            }${t("stats.of_total") || "% du total"}`}
                          />

                          <StatisticsCard
                            title={t("stats.new") || t("stats.new")}
                            count={statistics.new}
                            icon={Copy}
                            color="purple"
                            statusTypes={getStatusDescription(["1"])}
                            description={`${
                              statistics.total > 0
                                ? (
                                    (statistics.new / statistics.total) *
                                    100
                                  ).toFixed(1)
                                : 0
                            }${t("stats.of_total") || "% du total"}`}
                          />
                        </div>

                        {/* Chart */}
                        <StatisticsChart
                          orders={filteredOrders}
                          dateRange={dateRange}
                          startDate={startDate}
                          endDate={endDate}
                        />
                      </>
                    ),
                  },
                  {
                    key: "2",
                    label: t("stats.delivery_statistics"),
                    children: (
                      <DeliveryStatisticsView
                        filteredOrders={filteredOrders}
                        dateRange={dateRange}
                        setDateRange={setDateRange}
                        customDateRange={customDateRange}
                        setCustomDateRange={setCustomDateRange}
                        selected={selected}
                        setSelected={setSelected}
                        selectedConfirmerId={selectedConfirmerId}
                        setSelectedConfirmerId={setSelectedConfirmerId}
                        confirmers={confirmers}
                        products={products}
                        loading={loading}
                        getDateRangeValues={getDateRangeValues}
                        deliveryStatusMapping={deliveryStatusMapping}
                        t={t}
                        isRTL={isRTL}
                      />
                    ),
                  },
                ]}
              />
            </Spin>
          </main>
          <Footer />
        </div>
      </div>
    </div>
  );
};

//   export default OrderStatistics;
//                   );
//                 };

export default OrderStatistics;
